// Shared order-type model helpers.
//
// Order types live at users/{uid}/locations/{locId}/orderTypes/{typeId}.
// Historically each doc had only `{ name }` (free text). We now support a
// richer shape: { name, key, enabled, isCustom } where:
//   - key:      stable identifier ('delivery' | 'pickup' | 'dine_in' |
//               a slug derived from a custom name)
//   - enabled:  whether it's offered at all (you can disable without deleting)
//   - isCustom: true for anything that isn't one of the 3 presets
//
// The 3 presets map to the consumer apps' modes:
//   delivery → "Delivery", pickup → "Pick Up", dine_in → "Reserve".
//
// All readers are defensive: a legacy doc with only `{ name }` is normalized
// on read (key inferred from the name, enabled=true).

export const ORDER_TYPE_PRESETS = [
  { key: 'delivery', name: 'Delivery' },
  { key: 'pickup', name: 'Pick Up' },
  { key: 'dine_in', name: 'Dine In' },
];

/// Normalize a display name into a stable key. Maps common aliases of the
/// presets to their canonical key; anything else becomes a slug.
export function normalizeOrderTypeKey(name) {
  const n = (name || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (n === 'delivery') return 'delivery';
  if (n === 'pickup' || n === 'pick_up') return 'pickup';
  if (n === 'dine_in' || n === 'dinein' || n === 'dine_in_') return 'dine_in';
  return n;
}

export function isPresetOrderTypeKey(key) {
  return ORDER_TYPE_PRESETS.some((p) => p.key === key);
}

/// Normalize an orderType doc (legacy `{name}`-only or new shape) into the
/// full shape. Never mutates the input.
export function inferOrderType(id, data) {
  const name = (data?.name || id || '').toString();
  const key = (data?.key?.toString()) || normalizeOrderTypeKey(name);
  return {
    id,
    name: name || key,
    key,
    enabled: data?.enabled !== false, // legacy docs (no field) default to on
    isCustom: data?.isCustom === true || !isPresetOrderTypeKey(key),
  };
}

/// Keys of the enabled order types — this is exactly what gets published to
/// publicStores/{storeId}.orderTypes (array of strings) and what the
/// consumer apps filter on.
export function enabledOrderTypeKeys(orderTypes) {
  return orderTypes.filter((t) => t.enabled).map((t) => t.key);
}
